<template>
  <div class="admissionformContainer">
    <div class="admissionformAnswers bg-white" v-if="!close_page">
      <!-- Loading Spinner -->
      <div class="loading d-flex justify-content-center align-items-center" v-if="fetchData">
        <load-data></load-data>
      </div>

      <!-- Form Container -->
      <b-container class="p-3" :fluid="$screenSize > 600 ? false : true" v-else>
        <div v-if="isFormExpired" class="alert alert-danger text-center font-weight-bold">
          Form Expired!
        </div>

        <div v-else>
          <b-row>
            <b-col md="12">
              <!-- Submit Confirmation Modal -->
              <b-modal id="finishApproval" no-close-on-backdrop no-close-on-keyboard no-close-on-esc hide-footer
                hide-header centered size="xl">
                <div class="text-center">
                  <h5 class="text-danger font-weight-bold">
                    Are you sure you want to submit the form? Once submitted, it cannot be changed.
                  </h5>
                  <div class="mt-4">
                    <b-button size="lg" variant="primary" @click="prepareAnswersForApi(true)">Yes</b-button>
                    <b-button size="lg" variant="danger" class="ml-3"
                      @click="$bvModal.hide('finishApproval')">No</b-button>
                  </div>
                </div>
              </b-modal>

              <!-- Form Header -->
              <div id="studentSubmittedForm" v-if="studentAnswerSheet.length != 0">

                <div class="position-relative mb-2 pb-2 border-bottom text-center">
                  <button class="btn btn-danger btn-sm position-absolute" style="top: 0; right: 0;"
                    @click="showCloseConfirmation = true">
                    Close
                  </button>

                  <h6 class="text-primary font-weight-bold mb-0 d-inline-block">
                    {{ startAdmissionForm.onlinereg_with_questions.institution.name }} |
                    {{ startAdmissionForm.onlinereg_with_questions.name }}
                  </h6>
                </div>

                <b-modal v-model="showCloseConfirmation" title="Confirm Close" hide-footer>
                  <div class="mb-3">
                    Are you sure you want to close the page? <br>
                    Closing will discard any unsaved changes.
                  </div>

                  <div class="d-flex justify-content-end">
                    <button class="btn btn-secondary btn-sm mr-2" @click="showCloseConfirmation = false">
                      No
                    </button>
                    <button class="btn btn-danger btn-sm" @click="closeTab()">
                      Yes, Close
                    </button>
                  </div>
                </b-modal>

                <div v-if="applicationSubmittedTime" class="alert alert-success text-center font-weight-bold py-1 mb-2">
                  <small>Form Submitted Successfully!</small>
                </div>

                <b-form @submit.prevent="applicationSubmittedTime == null ? showWarningBeforeSubmission() : null">
                  <div v-if="applicationSubmittedTime == null"
                    class="d-flex justify-content-center align-items-center mb-3">
                    <b-button variant="success" size="sm" class="font-weight-bold shadow"
                      :disabled="saveChanges.disabled" @click="prepareAnswersForApi(false)">
                      {{ saveChanges.text }}
                    </b-button>

                    <!-- Add margin-left (gap) here -->
                    <b-button v-if="studentAnswerSheet.length" variant="primary" size="sm" type="submit"
                      class="font-weight-bold shadow ml-2" :disabled="responseBtn.disabled">
                      {{ responseBtn.text }}
                    </b-button>
                  </div>

                  <!-- Sections -->
                  <div v-for="(section, index) in sections" :key="index" v-show="currentSectionIndex === index">
                    <h5 class="text-center mb-2"><strong><u>{{ section.name }}</u></strong></h5>
                    <p class="text-info text-center small font-italic">{{ section.form_guideline }}</p>

                    <!-- Questions -->
                    <div v-for="(sheet, sheetIndex) in studentAnswerSheet[section.id]" :key="sheetIndex"
                      class="mb-2 pb-1 border-bottom">
                      <span v-if="sheet.type === 'title'" class="d-block text-center fw-bold">
                        <b>{{ sheet.name }}</b>
                      </span>
                      <div v-else>
                      <!-- Question -->
                      <div class="d-flex align-items-start mb-1">
                        <strong class="mr-2">{{ sheet.question_no }}.</strong>
                        <div class="flex-grow-1">
                          {{ sheet.name }}
                          <span v-if="sheet.input_attributes[0].answerMandatory" class="text-danger">*</span>
                        </div>
                      </div>

                      <!-- Text Answer -->
                      <div v-if="sheet.input_attributes[0].shortAnswer || sheet.input_attributes[0].paragraph">
                        <b-form-input :disabled="applicationSubmittedTime" v-model="sheet.studentAnswer"
                          placeholder="Enter Answer" class="form-control form-control-sm"
                          :class="{ 'form-group-error': $v.studentAnswerSheet[section.id].$each[sheetIndex].studentAnswer.$error }" />
                        <small class="text-danger"
                          v-if="$v.studentAnswerSheet[section.id].$each[sheetIndex].studentAnswer.$error && $v.studentAnswerSheet[section.id].$each[sheetIndex].studentAnswer.$dirty">
                          This field is required.
                        </small>
                      </div>

                      <!-- Multi Choice -->
                      <div v-if="sheet.input_attributes[0].multiChoice">
                        <b-form-checkbox-group :disabled="applicationSubmittedTime" v-model="sheet.multichoiceAns"
                          :options="sheet.choices" class="small" />
                        <small class="text-danger"
                          v-if="$v.studentAnswerSheet[section.id].$each[sheetIndex].multichoiceAns.$error && $v.studentAnswerSheet[section.id].$each[sheetIndex].multichoiceAns.$dirty">
                          Please select at least one option.
                        </small>
                      </div>

                      <!-- Single Choice -->
                      <div v-if="sheet.input_attributes[0].checkboxes">
                        <b-form-radio-group :disabled="applicationSubmittedTime" v-model="sheet.studentAnswer"
                          :options="sheet.choices" class="small" />
                        <small class="text-danger"
                          v-if="$v.studentAnswerSheet[section.id].$each[sheetIndex].studentAnswer.$error && $v.studentAnswerSheet[section.id].$each[sheetIndex].studentAnswer.$dirty">
                          Please select an option.
                        </small>
                      </div>

                      <!-- Dropdown -->
                      <div :disabled="applicationSubmittedTime" v-if="sheet.input_attributes[0].dropdown">
                        <b-form-select :disabled="applicationSubmittedTime" v-model="sheet.studentAnswer"
                          :options="sheet.choices" class="form-control form-control-sm" />
                        <small class="text-danger"
                          v-if="$v.studentAnswerSheet[section.id].$each[sheetIndex].studentAnswer.$error && $v.studentAnswerSheet[section.id].$each[sheetIndex].studentAnswer.$dirty">
                          Please select a value.
                        </small>
                      </div>

                      <!-- Attachments -->
                      <div v-if="sheet.allow_attachment">
                        <div class="d-flex align-items-center flex-wrap mt-1">
                          <span v-if="applicationSubmittedTime == null" class="text-info btn p-0 m-0 mr-2"
                            @click="newFileInputMask(sheet.id)">
                            <i class="fas fa-paperclip"></i>
                          </span>
                          <b-form-file :ref="'newFileInput' + sheet.id" class="d-none"
                            @change="newFileInput($event.target.files, sheet.id)" />

                          <span v-for="(file, fileIndex) in sheet.files" :key="fileIndex" class="mr-3 small">
                            <a :href="userAttachments + file.file_path + file.file_name" target="_blank">
                              {{ file.file_name }}
                            </a>
                            <i v-if="applicationSubmittedTime == null" class="fas fa-trash text-danger ml-2"
                              @click="deleteAnswerAttachment(section.id, sheetIndex, file.id, fileIndex)"></i>
                          </span>
                        </div>

                        <!-- <div v-if="newQuestionsAnswerAttachments[sheet.id]">
                        <b-progress :value="uploadPercentage[sheet.id].value" :max="100" show-progress animated class="mt-1"></b-progress>
                        <small v-for="(file, index) in newQuestionsAnswerAttachments[sheet.id]" :key="index">{{ file }}</small>
                      </div> -->
                      </div>
                      </div>


                    </div>

                    <!-- Pagination -->
                    <div class="mt-3 text-center">
                      <b-button v-if="index > 0" size="sm" @click="prevSection">Previous</b-button>
                      <b-button v-if="index < sections.length - 1" size="sm" class="ml-2"
                        @click="nextSection">Next</b-button>
                    </div>
                  </div>
                </b-form>
              </div>

              <div v-else class="alert alert-danger text-center mt-3 font-weight-bold">
                No questions found! Check back later.
              </div>
            </b-col>
          </b-row>
        </div>
      </b-container>
    </div>
    <span v-else>
      <p>Session closed successfully. To log in again, click below.</p>
      <button class="btn btn-primary" @click="goToLogin">Login Page</button>
    </span>
  </div>
</template>



<style scoped>
.admissionformAnswers {
  width: 100vw;
  padding: 40px;
  /* background-color: #ffffff; */
}

.admissionformContainer {
  padding: 40px;
  background-color: #f5f5f5;
  /* same as body */
}

.form-group-error {
  border: 1px solid red !important;
}

.input-error-message,
.text-danger {
  font-size: 0.8rem;
}

b-form-input,
b-form-select,
b-form-radio-group,
b-form-checkbox-group {
  margin-bottom: 0.5rem;
}

.border-bottom {
  border-bottom: 1px solid #dee2e6 !important;
}
</style>
<script>
import { requiredIf } from "vuelidate/lib/validators";
import moment from "moment";
export default {
  props: {
    admission_form_id: String,
    email: String
  },
  async created() {
    await this.initiateAdmissionForm();
    this.userAttachments = await this.$store.getters.getAPIKey.userAttachments;
  },
  data() {
    return {
      close_page: false,
      showCloseConfirmation: false,
      currentSectionIndex: 0,
      fetchData: false,
      userAttachments: null,
      loadAnswer: false,
      registrationDetails: {},
      startAdmissionForm: {},
      studentAnswerSheet: [],
      newQuestionsAnswerAttachments: {},
      uploadPercentage: {},
      responseBtn: {
        text: "Submit",
        disabled: false,
      },
      saveChanges: {
        text: "Save Changes",
        disabled: false,
      },
      questions: [],
      sections: [],
      answers: [],
      applicationSubmittedTime: null,
      userId: null,
    };
  },
  computed: {
    isFormExpired() {
      if (!this.startAdmissionForm?.onlinereg_with_questions?.end_date) {
        return false;
      }

      const endDate = new Date(this.startAdmissionForm.onlinereg_with_questions.end_date);
      const today = new Date();

      return endDate < today;
    }
  },
  validations() {
    const baseRules = {
      files: {
        required: requiredIf((item) => {
          return (
            item.input_attributes?.[0]?.answerMandatory &&
            item.allow_attachment
          );
        }),
      },
      studentAnswer: {
        required: requiredIf((item) => {
          const attr = item.input_attributes?.[0];
          return (
            attr?.answerMandatory &&
            (attr.shortAnswer || attr.paragraph || attr.checkboxes || attr.dropdown)
          );
        }),
      },
      multichoiceAns: {
        required: requiredIf((item) => {
          const attr = item.input_attributes?.[0];
          return attr?.answerMandatory && attr.multiChoice;
        }),
      },
    };

    const validationsObj = { studentAnswerSheet: {} };

    // for each sectionId in your data, add $each with baseRules
    Object.keys(this.studentAnswerSheet).forEach((sectionId) => {
      validationsObj.studentAnswerSheet[sectionId] = {
        $each: baseRules,
      };
    });

    return validationsObj;
  },
  methods: {
    goToLogin() {
      this.$router.push(`/admission_form/${this.admission_form_id}`).then(() => {
        window.location.reload();
      });
    },
    closeTab() {
      this.showCloseConfirmation = false;
      this.close_page = true;
    },
    handleLogout() {
      this.$emit("logout"); // Emit logout event
    },
    nextSection() {
      if (this.currentSectionIndex < this.sections.length - 1) {
        this.currentSectionIndex++;
      }
    },
    prevSection() {
      if (this.currentSectionIndex > 0) {
        this.currentSectionIndex--;
      }
    },
    storeAnswerFromCheckboxes(sheetIndex) {
      this.sections.forEach((section) => {
        const sheet = this.studentAnswerSheet[section.id];

        // Check if the sheet exists and has an item at the given index
        if (sheet && sheet[sheetIndex]) {
          const multichoicesOptions = sheet[sheetIndex].multichoiceAns || [];

          // Clear current answer
          sheet[sheetIndex].studentAnswer = null;

          // Join selected answers as a string (e.g., "Option1,Option2")
          sheet[sheetIndex].studentAnswer = multichoicesOptions.join();
        }
      });
    },
    async initiateAdmissionForm() {
      this.fetchData = true;
      const url =
        this.$store.getters.getAPIKey.mainAPI +
        `/admissionforms/publicform`;
      this.$axios
        .post(url, {
          admission_form_id: this.admission_form_id,
          email: this.email
        })
        .then((response) => {
          this.startAdmissionForm = response.data;
          this.sections = this.startAdmissionForm.onlinereg_with_questions.admissionformsections;
          this.answers = this.startAdmissionForm.application_with_answers.answers;
          this.userId = this.startAdmissionForm.application_with_answers.id;
          this.applicationSubmittedTime = this.startAdmissionForm.application_with_answers.end_at;
          this.prepareAnswerSheet();


        })
        .catch((error) => {
          error;
        });
    },
    async prepareAnswerSheet() {
      await this.getAdmissionQuestions();
      await this.getApprovalAnswers();
      this.fetchData = false;
    },
    async showWarningBeforeSubmission() {
      await this.$v.$touch();
      if (this.$v.$invalid) {
        this.submitStatus = "ERROR";
      } else {
        this.$bvModal.show("finishApproval");
      }
    },
    async prepareAnswersForApi(status) {
      this.$bvModal.hide("finishApproval");
      this.responseBtn.text = "Please wait";
      this.responseBtn.disabled = true;
      this.saveChanges.text = "Saving...";
      this.saveChanges.disabled = true;
      this.loadAnswer = true;

      let answersPostData = await {
        onlinereganswers: [],
        application_id: this.startAdmissionForm.application_with_answers.id,
        final_submission: !status && status != undefined ? false : true,
      };
      for (const section of this.sections) {
       const sheet = (this.studentAnswerSheet[section.id] || []).filter(item => item.type !== 'title');
        for (const ans of sheet) {
          answersPostData.onlinereganswers.push({
            question_id: ans.id,
            answer:
              ans.multichoiceAns?.length === 0
                ? ans.studentAnswer
                : ans.multichoiceAns.join(),
            attachment: "", // Add file handling if needed
          });
        }
      }
      let storeAnswer = await this.storeAnswersWithApi(answersPostData);
      if (storeAnswer) {
        await this.$toast.success(
          !status ? "Changes Saved" : "Response Submitted.",
          {
            position: "top",
            duration: 3000,
          }
        );
        console.log("status", status);
        this.clearResponse(!status && status != undefined ? false : true);
      }
      if (!storeAnswer) {
        this.$toast.error("Something went wrong!, Please try again", {
          position: "top",
          duration: 3000,
        });
        return this.clearResponse(false);
      }
    },
    async storeAnswersWithApi(answersPostData) {
      let status = null;
      const url =
        this.$store.getters.getAPIKey.mainAPI + `/admissionformsanswers`;
      await this.$axios
        .post(url, answersPostData)
        .then((response) => {
          status = true;
          response;
        })
        .catch((error) => {
          status = false;
          error;
        });
      return status;
    },
    async uploadAttachment(file, questionId) {
      let formData = await new FormData();

      // let attachmentData = await {
      //   question_id: questionId,
      //   answer: null,
      //   application_id: this.startAdmissionForm.application_with_answers.id,
      //   attachment: file,
      // };
      await formData.append("question_id", questionId);
      await formData.append("answer", null);
      await formData.append(
        "application_id",
        this.startAdmissionForm.application_with_answers.id
      );
      await formData.append("attachment", file);
      // await formData.append("file", file);

      const url =
        this.$store.getters.getAPIKey.mainAPI +
        `/admissionformsanswers/uploadfile`;
      await this.$axios
        .post(url, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage[questionId].value = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
        .then((response) => {
          response;
        })
        .catch((error) => {
          error;
        });
      // file;
      // this.uploadPercentage[questionId]["value"] = await 50;
    },
    async deleteAnswerAttachment(sectionId, sheetIndex, fileId, fileIndex) {
      this.loadAnswer = true;
      const url =
        this.$store.getters.getAPIKey.mainAPI +
        `/admissionformsanswers/attachments/${fileId}`;
      await this.$axios
        .delete(url)
        .then((response) => {
          this.studentAnswerSheet[sectionId][sheetIndex].files.splice(fileIndex, 1);
          this.$toast.success("Attachment Deleted.", {
            position: "top",
            duration: 3000,
          });
          this.loadAnswer = false;
          response;
        })
        .catch((error) => {
          this.$toast.error("Something Went wrong, Please try again!.", {
            position: "top",
            duration: 3000,
          });
          this.loadAnswer = false;
          error;
        });
    },
    getRegistrationDetails() {
      this.fetchData = true;
      const surveyApprovalId = this.$route.params.approval_id;
      const url =
        this.$store.getters.getAPIKey.surveyApprovals + `/${surveyApprovalId}`;
      this.$axios
        .get(url)
        .then((response) => {
          this.registrationDetails = {
            id: response.data.id,
            name: response.data.name,
            summary: response.data.summary,
            academic_year: response.data.academic_year,
            start_at: new Date(
              moment(
                moment
                  .utc(response.data.start_at, "YYYY-MM-DD HH:mm:ss")
                  .toDate()
              ).format()
            ),
            end_at: new Date(
              moment(
                moment.utc(response.data.end_at, "YYYY-MM-DD HH:mm:ss").toDate()
              ).format()
            ),
            grouping_tag: response.data.grouping_tag,
            comment_feedback:
              response.data.comment_feedback == 1 ? true : false,
            hidden: response.data.hidden == 1 ? true : false,
            participants_only:
              response.data.participants_only == 1 ? true : false,
            is_anonymous: response.data.is_anonymous == 1 ? true : false,
          };

          response;
        })
        .catch((error) => {
          this.fetchData = false;
          error;
        });
    },

    async getAdmissionQuestions() {
      this.studentAnswerSheet = [];

      this.sections.forEach((section) => {
        // Ensure section ID key is initialized as an array
        if (!this.studentAnswerSheet[section.id]) {
          this.studentAnswerSheet[section.id] = [];
        }

        let merged = [];

        // Normalize questions
        if (section.questions && section.questions.length !== 0) {
          section.questions.forEach((ques) => {
            let choices = ques.input_attributes?.[0]?.dropdown
              ? [{ value: null, text: "Select One" }]
              : [];

            if (ques.answers && ques.answers.length !== 0) {
              ques.answers.forEach((choice) => {
                choices.push({ value: choice, text: choice });
              });
            }

            merged.push({
              id: ques.id,
              type: ques.type ?? "question",
              order_no: parseInt(ques.question_no, 10),
              question_no: ques.question_no,
              name: ques.name,
              summary: ques.summary,
              choices: choices,
              allow_attachment: ques.allow_attachment == 1,
              input_attributes: ques.input_attributes,
              studentAnswer: null,
              onlineregistration_id: ques.onlineregistration_id,
              grouping_tag: ques.grouping_tag,
              multichoiceAns: [],
              files: [],
              disabled: true,
            });
          });
        }

        // Normalize titles (if they exist in section)
        if (section.titles && section.titles.length !== 0) {
          section.titles.forEach((t) => {
            merged.push({
              id: t.id,
              type: "title",
              order_no: parseInt(t.si_no, 10),
              si_no: t.si_no,
              name: t.name,
              disabled: true,
            });
          });
        }

        // Sort everything by order_no, titles come before questions if equal
        merged.sort((a, b) => {
          if (a.order_no === b.order_no) {
            if (a.type === "title" && b.type !== "title") return -1;
            if (a.type !== "title" && b.type === "title") return 1;
            return 0;
          }
          return a.order_no - b.order_no;
        });

        this.studentAnswerSheet[section.id] = merged;
      });

      this.fetchData = false;
    },

    async getApprovalAnswers() {
      for (const section of this.sections) {
        const sheet = this.studentAnswerSheet[section.id] || [];

        for (const ques of sheet) {
          const questionId = ques.id;

          // Find the matching answer
          const matchingAnswer = this.answers.find(
            (ans) => ans.question_id === questionId
          );

          if (matchingAnswer) {
            ques.studentAnswer = matchingAnswer.answer;
            ques.files = matchingAnswer.files || [];

            ques.multichoiceAns =
              ques.input_attributes?.[0]?.multiChoice && matchingAnswer.answer
                ? matchingAnswer.answer.split(",")
                : [];
          }
        }
      }

      this.fetchData = false;
    },
    async getAnswer(questionId) {
      this.fetchData = true;

      // Find the question object and its location: sectionId and index
      let found = null;

      for (const sectionId in this.studentAnswerSheet) {
        const questions = this.studentAnswerSheet[sectionId];
        const index = questions.findIndex((ques) => ques.id === questionId);
        if (index !== -1) {
          found = { sectionId, index };
          break;
        }
      }

      if (!found) {
        // Question not found in answer sheet
        this.fetchData = false;
        return;
      }

      try {
        const url =
          this.$store.getters.getAPIKey.mainAPI +
          `/admissionformsapplications/${this.startAdmissionForm.application_with_answers.id}/onlinereganswers`;

        const response = await this.$axios.get(url);
        const answers = response.data;

        // Find the answer for the questionId
        const answer = answers.find((ans) => ans.question_id === questionId);

        if (answer) {
          this.studentAnswerSheet[found.sectionId][found.index].files = answer.files || [];
        }

        this.fetchData = false;
        this.$forceUpdate();
      } catch (error) {
        this.fetchData = false;
        // You might want to log or handle the error here
        console.error(error);
      }
    },
    async newFileInput(file, questionId) {
      this.$set(this.newQuestionsAnswerAttachments, questionId, []);
      this.newQuestionsAnswerAttachments[questionId].push(file.name);

      this.$set(this.uploadPercentage, questionId, {});
      this.$set(this.uploadPercentage[questionId], "value", 0);

      let index = this.newQuestionsAnswerAttachments[questionId].indexOf(
        file.name
      );

      await this.uploadAttachment(file[0], questionId);

      await this.newQuestionsAnswerAttachments[questionId].splice(index, 1);

      this.getAnswer(questionId);
      // this.initiateAdmissionForm();
    },
    newFileInputMask(questionId) {
      let elementReference = `${"newFileInput" + questionId}`;
      this.$refs[elementReference][0].$refs.input.click();
    },
    clearResponse(status) {
      console.log("status", status);
      this.responseBtn.text = "Submit";
      this.saveChanges.text = "Save Changes";

      this.responseBtn.disabled = false;

      this.saveChanges.disabled = false;
      this.loadAnswer = false;
      setTimeout(() => {
        if (status) {
          this.initiateAdmissionForm();
        }
      }, 3000);
    },
  },
};
</script>
